/**
 * 보상형 광고 후 PRO 연장 — tori-todo `rewardedAdService`와 동일 계약.
 * WebView: `requestRewardedAd` → 네이티브 시청 완료 후 `upgrade-pro`.
 * 웹 브라우저: 광고 없이 `upgrade-pro`만 호출(백엔드 정책과 동일).
 */
import { isWebView, requestRewardedAd } from '@/utils/inAppPurchase'
import { userApi } from '@/services/api/user.api'

export type WatchRewardedAdResult =
  | { success: true }
  | {
      success: false
      reason: 'api_error' | 'ad_failed'
      message?: string
      errorCode?: 'E007' | 'E008'
      minutesUntilNext?: number
    }

async function resolveMinutesUntilNext(): Promise<number | undefined> {
  try {
    const status = await userApi.getAdRewardStatus()
    if (status?.nextAvailableAt) {
      const next = new Date(status.nextAvailableAt).getTime()
      if (!Number.isNaN(next)) {
        return Math.max(0, Math.ceil((next - Date.now()) / 60000))
      }
    }
  } catch {
    // ignore
  }
  return undefined
}

export async function watchRewardedAdAndUpgradePro(): Promise<WatchRewardedAdResult> {
  try {
    if (isWebView()) {
      await requestRewardedAd()
    }
    await userApi.upgradeToProByAd()
    return { success: true }
  } catch (e: unknown) {
    const err = e as { message?: string; response?: { data?: { message?: string; errorCode?: string } } }
    const rawMsg = String(err?.message ?? '')
    if (rawMsg.includes('취소') || rawMsg.toLowerCase().includes('cancel')) {
      return { success: false, reason: 'ad_failed', message: rawMsg }
    }

    const payload = err?.response?.data
    const errorCode = payload?.errorCode as 'E007' | 'E008' | undefined
    const message =
      (typeof payload?.message === 'string' && payload.message) ||
      rawMsg ||
      'PRO 연장에 실패했습니다.'

    let minutesUntilNext: number | undefined
    if (errorCode === 'E007' || errorCode === 'E008') {
      minutesUntilNext = await resolveMinutesUntilNext()
    }

    const adLoadFail =
      rawMsg.includes('광고가 준비되지 않아') ||
      rawMsg.toLowerCase().includes('no fill') ||
      rawMsg.toLowerCase().includes('no-fill')

    return {
      success: false,
      reason: adLoadFail ? 'ad_failed' : 'api_error',
      message,
      errorCode,
      minutesUntilNext,
    }
  }
}

/** 웹에서도 백엔드 연동으로 PRO 연장 시도 가능 */
export function isRewardedAdFlowAvailable(): boolean {
  return true
}
